/**
 * The book view's wheel and trackpad turning (decision 70; part 15a, table WH): one turn per gesture. A gesture is a run
 * of wheel events without a pause; it turns once, when it has moved far enough along its main axis, and the rest of it
 * (a trackpad's momentum) turns nothing more. A wheel event with Ctrl or Meta is the browser's zoom (a trackpad pinch
 * is one), never a turn.
 */

/** WH1: a pause this long between two wheel events ends a gesture, in milliseconds. */
export const WHEEL_PAUSE_MS = 200;
/** WH2: how far a gesture moves before it turns, in CSS pixels. */
export const WHEEL_TURN_PX = 40;
/** WH2: CSS pixels a line counts (`deltaMode` 1) and a page (`deltaMode` 2). */
export const WHEEL_LINE_PX = 16;
export const WHEEL_PAGE_PX = 400;

/** What a wheel event says, as far as turning goes. */
export interface WheelInput {
  readonly deltaX: number;
  readonly deltaY: number;
  /** 0 pixels, 1 lines, 2 pages (the DOM's `WheelEvent.deltaMode`). */
  readonly deltaMode: number;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  /** When it happened, in milliseconds (`timeStamp`). */
  readonly time: number;
}

/** WH4: whether a wheel event is the browser's zoom, not the book's. */
export function isZoom(input: WheelInput): boolean {
  return input.ctrlKey || input.metaKey;
}

/** Table WH: a gesture's state, one per pages area. */
export class WheelTurns {
  #last = Number.NEGATIVE_INFINITY;
  #x = 0;
  #y = 0;
  #turned = false;

  /** WH1–WH4: the turn this event makes: forward (1), back (−1) or none (0). */
  take(input: WheelInput): 1 | -1 | 0 {
    if (isZoom(input)) return 0;
    // WH1: a pause ends the gesture; this event starts the next.
    if (input.time - this.#last >= WHEEL_PAUSE_MS) {
      this.#x = 0;
      this.#y = 0;
      this.#turned = false;
    }
    this.#last = input.time;
    // WH3: a gesture turns once.
    if (this.#turned) return 0;
    const unit = input.deltaMode === 1 ? WHEEL_LINE_PX : input.deltaMode === 2 ? WHEEL_PAGE_PX : 1;
    this.#x += input.deltaX * unit;
    this.#y += input.deltaY * unit;
    // WH2: along its main axis, down or right forward.
    const along = Math.abs(this.#x) > Math.abs(this.#y) ? this.#x : this.#y;
    if (Math.abs(along) < WHEEL_TURN_PX) return 0;
    this.#turned = true;
    return along > 0 ? 1 : -1;
  }
}
